import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  Outlet,
} from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { logoutUsuarioAction } from "./redux/actions/loginActions";
import { getCookie } from "./utils/cookieUtils";

import "./App.css";

// Components
import {
  Login,
  RecordarContrasena,
  ListaRutas,
  ListaPedidos,
  Perfil,
  Contrasena,
  NotFound,
} from "./components";

// Layout
import { Header, Footer, SideNav } from "./components/Layout";

function App() {
  const isAuthenticated = useSelector((state) => state.login.autenticado);
  const [mostrarModalSesion, setMostrarModalSesion] = useState(false);
  const dispatch = useDispatch();

  const PrivateRoute = ({ isAuthenticated }) => {
    if (isAuthenticated) {
      setMostrarModalSesion(true);
      return <Outlet />;
    } else {
      return <Navigate to="/" />;
    }
  };

  const useCerrarSidebarResponsive = () => {
    useEffect(() => {
      const body = document.body;
      const sidebar = document.querySelector(".main-sidebar");
      const menuToggle = document.querySelector('[data-widget="pushmenu"]');
      const menuLinks = document.querySelectorAll(".nav-sidebar a");

      const handleMenuLinkClick = () => {
        if (body.classList.contains("sidebar-open")) {
          menuToggle?.click();
        }
      };

      const handleOutsideClick = (e) => {
        if (
          body.classList.contains("sidebar-open") &&
          !sidebar.contains(e.target) &&
          !e.target.closest('[data-widget="pushmenu"]')
        ) {
          menuToggle?.click();
        }
      };

      // Escuchar clics en links del menú
      menuLinks.forEach((link) => {
        link.addEventListener("click", handleMenuLinkClick);
      });

      // Escuchar clics fuera del menú
      document.addEventListener("click", handleOutsideClick);

      // Limpieza
      return () => {
        menuLinks.forEach((link) => {
          link.removeEventListener("click", handleMenuLinkClick);
        });
        document.removeEventListener("click", handleOutsideClick);
      };
    }, []);
  };

  const MainLayout = () => {
    useCerrarSidebarResponsive();
    return (
      <>
        <Header />
        <div className="content-wrapper">
          {/* <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-12">
                <h1 className="m-0">Eventos</h1>
              </div>
            </div>
          </div>
        </div> */}
          <div className="content">
            <div className="container-fluid">
              <Outlet />
            </div>
          </div>
        </div>
        <SideNav />
        <Footer />
      </>
    );
  };

  // Verifica la cookie de sesión al volver de background (fix iOS Safari)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isAuthenticated) {
        const sessionCookie = getCookie("token");
        if (!sessionCookie) {
          Swal.close();
          setMostrarModalSesion(false);
          dispatch(logoutUsuarioAction());
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, dispatch]);

  const handleOnIdle = () => {
    if (mostrarModalSesion) {
      Swal.fire({
        title: "Vencimiento de sesión",
        text: "La sesión ha vencido, debe ingresar nuevamente.",
        denyButtonText: "Salir",
      }).then((result) => {
        if (result.isConfirmed) {
          setMostrarModalSesion(false);
          dispatch(logoutUsuarioAction());
          Swal.close();
        }
      });
    }
  };

  useIdleTimer({
    timeout: 86400000, // 24 horas — consistente con la expiración de la cookie de sesión
    onIdle: handleOnIdle,
  });

  return (
    <Router>
      <Routes>
        {/* Ruta Pública */}
        <Route exact path="/" element={<Login />} />
        <Route
          exact
          path="/recordarcontrasena"
          element={<RecordarContrasena />}
        />
        {/* Rutas Privadas */}
        <Route element={<PrivateRoute isAuthenticated={isAuthenticated} />}>
          <Route element={<MainLayout />}>
            <Route exact path="/panel" element={<ListaRutas />} />
            <Route exact path="/panel/entregas" element={<ListaRutas />} />
            <Route path="/ruta/:id" element={<ListaPedidos />} />
            <Route exact path="/panel/perfil" element={<Perfil />} />
            <Route exact path="/panel/contrasena" element={<Contrasena />} />
          </Route>
        </Route>

        {/* Ruta para página no encontrada */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
